import { 
  logEvent, 
  setUserProperties, 
  setUserId, 
  EventParams
} from 'firebase/analytics';
import { analytics } from './firebase';

// Custom event names for the audio application
export const ANALYTICS_EVENTS = {
  // Authentication events
  LOGIN: 'login' as const,
  LOGOUT: 'logout' as const,
  SIGNUP: 'sign_up' as const,
  
  // Audio events
  AUDIO_UPLOAD: 'audio_upload' as const,
  AUDIO_PROCESS: 'audio_process' as const,
  AUDIO_ANALYZE: 'audio_analyze' as const,
  
  // Fourier events
  FOURIER_START: 'fourier_start' as const,
  DRAWING_CREATE: 'drawing_create' as const,
  FOURIER_EXPORT: 'fourier_export' as const,
  
  // Wave Lab events
  WAVE_CONFIG_CREATE: 'wave_config_create' as const,
  WAVE_CONFIG_LOAD: 'wave_config_load' as const,
  WAVE_CONFIG_SAVE: 'wave_config_save' as const,
  WAVE_CONFIG_DELETE: 'wave_config_delete' as const,
  
  // Analysis events
  ANALYSIS_START: 'analysis_start' as const,
  ANALYSIS_SAVE: 'analysis_save' as const,
  ANALYSIS_LOAD: 'analysis_load' as const,
  ANALYSIS_DELETE: 'analysis_delete' as const,
  
  // UI events
  PAGE_VIEW: 'page_view' as const,
  FEATURE_USE: 'feature_use' as const,
  ERROR_ENCOUNTERED: 'error_encountered' as const,
} as const;

type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

class AnalyticsService {
  private isEnabled: boolean = false;

  constructor() {
    this.isEnabled = analytics !== null && typeof window !== 'undefined';
  }

  /**
   * Set the current user ID for analytics
   */
  setUser(userId: string): void {
    if (!this.isEnabled || !analytics) return;
    
    try {
      setUserId(analytics, userId);
    } catch (error) {
      console.warn('Analytics setUser failed:', error);
    }
  }

  /**
   * Set user properties for analytics
   */
  setUserProperties(properties: { [key: string]: string | number | boolean }): void {
    if (!this.isEnabled || !analytics) return;
    
    try {
      setUserProperties(analytics, properties);
    } catch (error) {
      console.warn('Analytics setUserProperties failed:', error);
    }
  }

  /**
   * Track a generic event
   */
  trackEvent(eventName: AnalyticsEventName | string, parameters?: EventParams): void {
    if (!this.isEnabled || !analytics) return;
    
    try {
      logEvent(analytics, eventName, parameters);
    } catch (error) {
      console.warn('Analytics trackEvent failed:', error);
    }
  }

  // Authentication tracking
  trackLogin(method: string): void {
    this.trackEvent(ANALYTICS_EVENTS.LOGIN, { method });
  }

  trackLogout(): void {
    this.trackEvent(ANALYTICS_EVENTS.LOGOUT);
  }

  trackSignup(method: string): void {
    this.trackEvent(ANALYTICS_EVENTS.SIGNUP, { method });
  }

  // Audio tracking
  trackAudioUpload(fileSize: number, duration?: number, format?: string): void {
    this.trackEvent(ANALYTICS_EVENTS.AUDIO_UPLOAD, {
      file_size: fileSize,
      duration: duration,
      format: format
    });
  }

  trackAudioProcess(processType: string, duration?: number): void {
    this.trackEvent(ANALYTICS_EVENTS.AUDIO_PROCESS, {
      process_type: processType,
      duration: duration
    });
  }

  trackAudioAnalyze(analysisType: string, sampleRate?: number): void {
    this.trackEvent(ANALYTICS_EVENTS.AUDIO_ANALYZE, {
      analysis_type: analysisType,
      sample_rate: sampleRate
    });
  }

  // Fourier Lab tracking
  trackFourierStart(): void {
    this.trackEvent(ANALYTICS_EVENTS.FOURIER_START);
  }

  trackDrawingCreate(complexity: number): void {
    this.trackEvent(ANALYTICS_EVENTS.DRAWING_CREATE, {
      complexity: complexity
    });
  }

  trackFourierExport(format: string): void {
    this.trackEvent(ANALYTICS_EVENTS.FOURIER_EXPORT, {
      format: format
    });
  }

  // Wave Lab tracking
  trackWaveConfigCreate(configType: string): void {
    this.trackEvent(ANALYTICS_EVENTS.WAVE_CONFIG_CREATE, {
      config_type: configType
    });
  }

  trackWaveConfigLoad(configId: string): void {
    this.trackEvent(ANALYTICS_EVENTS.WAVE_CONFIG_LOAD, {
      config_id: configId
    });
  }

  trackWaveConfigSave(configId: string, isNew: boolean): void {
    this.trackEvent(ANALYTICS_EVENTS.WAVE_CONFIG_SAVE, {
      config_id: configId,
      is_new: isNew
    });
  }

  trackWaveConfigDelete(configId: string): void {
    this.trackEvent(ANALYTICS_EVENTS.WAVE_CONFIG_DELETE, {
      config_id: configId
    });
  }

  // Analysis tracking
  trackAnalysisStart(analysisType: string): void {
    this.trackEvent(ANALYTICS_EVENTS.ANALYSIS_START, {
      analysis_type: analysisType
    });
  }

  trackAnalysisSave(analysisId: string, analysisType: string): void {
    this.trackEvent(ANALYTICS_EVENTS.ANALYSIS_SAVE, {
      analysis_id: analysisId,
      analysis_type: analysisType
    });
  }

  trackAnalysisLoad(analysisId: string): void {
    this.trackEvent(ANALYTICS_EVENTS.ANALYSIS_LOAD, {
      analysis_id: analysisId
    });
  }

  trackAnalysisDelete(analysisId: string): void {
    this.trackEvent(ANALYTICS_EVENTS.ANALYSIS_DELETE, {
      analysis_id: analysisId
    });
  }

  // UI tracking
  trackPageView(pageName: string): void {
    this.trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }

  trackFeatureUse(featureName: string, context?: string): void {
    this.trackEvent(ANALYTICS_EVENTS.FEATURE_USE, {
      feature_name: featureName,
      context: context
    });
  }

  trackError(errorType: string, errorMessage?: string, context?: string): void {
    this.trackEvent(ANALYTICS_EVENTS.ERROR_ENCOUNTERED, {
      error_type: errorType,
      error_message: errorMessage?.substring(0, 100), // Limit message length
      context: context
    });
  }

  // Performance tracking
  trackPerformance(action: string, duration: number, context?: string): void {
    this.trackEvent('performance_metric', {
      action: action,
      duration: duration,
      context: context
    });
  }

  // Custom dimension tracking
  trackCustomDimension(dimension: string, value: string | number): void {
    this.trackEvent('custom_dimension', {
      [dimension]: value
    });
  }
}

// Export a singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;